import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BIZ_ADDRESS, CONTACT_PHONE, CONTACT_EMAIL } from './common/SiteFooter'; // 사업자정보 상수 공용 (토스 심사: 상품 페이지 하단 사업자정보 필수)

/**
 * 기공소용 랜딩 페이지 (/lab) — 비회원, 풀스크린
 * 배너/전단 QR의 착지점. 타깃: 기공소 사장.
 * 핵심 메시지: CAD 폴더만 연결하면 월말 거래명세서 자동화.
 */
const C = {
    ink: '#151b26', sub: '#5b6472', faint: '#8b93a1', hair: '#e6e8ec', bg: '#f5f6f8',
    accent: '#274690', accentSoft: '#eef1f8', ok: '#157f3d', okSoft: '#e9f5ee',
};
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Pretendard", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif';

const STEPS = [
    { n: '1', title: '에이전트 설치', desc: '기공소 PC에 덴클 에이전트를 설치합니다. 3분이면 끝나고, 이후에는 트레이에서 조용히 동작합니다.' },
    { n: '2', title: 'CAD 작업 폴더 연결', desc: '[+ CAD 작업 폴더 연결]로 exocad·3shape 작업 폴더를 등록하면 환자명·치과·작업일을 알아서 인식합니다. 지난 작업까지 한 번에 수집할 수 있습니다.' },
    { n: '3', title: '월말 명세서 자동 완성', desc: '거래명세서 작성 화면에서 치과를 고르면 그 달 수집분이 자동으로 채워집니다. 발행·정산·세금계산서까지 그대로 이어집니다.' },
];

const FEATURES = [
    { icon: '📂', title: '작업 내역 자동 수집', desc: 'exocad·3shape 작업 폴더를 자동 분석해 환자·치과별 작업 내역을 매일 수집합니다. 월말에 몰아서 타이핑할 필요가 없습니다.' },
    { icon: '📝', title: '의뢰서 자동 등록', desc: '수집된 작업은 의뢰서로 자동 등록됩니다. 따로 입력하지 않아도 되고, 잘못 잡힌 건은 거래명세서 작성 화면에서 바로 제외·복원할 수 있습니다.' },
    { icon: '🔁', title: '리메이크 자동 감지', desc: '재제작 건을 자동으로 감지하고, 환자명에 섞인 쉐이드·메모까지 자동으로 분리합니다. 리메이크 현황이 한눈에 정리됩니다.' },
    { icon: '💰', title: '단가 자동 완성', desc: '치과별 단가는 한 번만 입력하면 다음 달부터 자동 완성됩니다. 명세서 금액 계산이 저절로 끝납니다.' },
    { icon: '🧾', title: '전자세금계산서 발행', desc: '완성된 거래명세서에서 바로 전자세금계산서까지 발행합니다. 정산 상태도 함께 관리됩니다.' },
    { icon: '🏥', title: '거래 치과 초대', desc: '거래 중인 치과를 웹으로 초대하면, 치과가 의뢰서를 웹에서 직접 보내옵니다. 팩스·전화 의뢰가 줄어듭니다.' },
];

function LabLanding() {
    const navigate = useNavigate();
    const goSignup = () => navigate('/signup?type=lab');   // 가입 화면에서 기공소 유형 프리셋 + 정보 입력 단계 직행

    const cta = (label, style = {}) => (
        <button onClick={goSignup} style={{
            background: C.accent, color: '#fff', border: 'none', borderRadius: 10,
            padding: '16px 30px', fontSize: 16, fontWeight: 800, cursor: 'pointer',
            fontFamily: 'inherit', letterSpacing: '-0.3px', boxShadow: '0 6px 18px rgba(39,70,144,.28)',
            ...style,
        }}>{label}</button>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT, color: C.ink }}>
            {/* 상단 바 */}
            <div style={{ background: '#fff', borderBottom: `1px solid ${C.hair}`, position: 'sticky', top: 0, zIndex: 10 }}>
                <div style={{ maxWidth: 960, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: C.accent, color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>DC</div>
                    <b style={{ fontSize: 15, letterSpacing: '-0.3px' }}>덴탈클러스터</b>
                    <button onClick={goSignup} style={{ marginLeft: 'auto', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                        무료로 시작
                    </button>
                </div>
            </div>

            {/* 1. 히어로 */}
            <div style={{ background: `linear-gradient(180deg, ${C.accentSoft} 0%, #fff 100%)` }}>
                <div style={{ maxWidth: 960, margin: '0 auto', padding: '64px 20px 56px', textAlign: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 2, color: C.accent }}>기공소 사장님께</div>
                    <h1 style={{ fontSize: 'clamp(30px, 6vw, 44px)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.25, marginTop: 14 }}>
                        월말 밤샘 타이핑,<br />이제 제로.
                    </h1>
                    <p style={{ fontSize: 'clamp(15px, 2.5vw, 18px)', color: C.sub, lineHeight: 1.7, marginTop: 16, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                        CAD 폴더만 연결하면<br />
                        <b style={{ color: C.ink }}>거래명세서가 자동으로 만들어집니다</b>
                    </p>
                    <div style={{ marginTop: 30 }}>
                        {cta('1개월 무료로 시작하기')}
                    </div>
                    <div style={{ fontSize: 13, color: C.faint, marginTop: 12 }}>
                        카드 등록 없이 바로 시작 · 카드를 등록하기 전에는 결제가 발생하지 않습니다
                    </div>
                </div>
            </div>

            {/* 2. 작동 방식 3단계 */}
            <div style={{ maxWidth: 960, margin: '0 auto', padding: '56px 20px' }}>
                <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.7px', textAlign: 'center' }}>이렇게 동작합니다</h2>
                <p style={{ fontSize: 14, color: C.sub, textAlign: 'center', marginTop: 8 }}>설치 후 하는 일은 폴더 연결 한 번뿐입니다.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, marginTop: 32 }}>
                    {STEPS.map(s => (
                        <div key={s.n} style={{ background: C.bg, border: `1px solid ${C.hair}`, borderRadius: 14, padding: '26px 22px' }}>
                            <div style={{ width: 34, height: 34, borderRadius: '50%', background: C.accent, color: '#fff', fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</div>
                            <div style={{ fontSize: 16.5, fontWeight: 800, marginTop: 14, letterSpacing: '-0.3px' }}>{s.title}</div>
                            <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.7, marginTop: 8 }}>{s.desc}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 3. 기능 6개 */}
            <div style={{ background: C.bg, borderTop: `1px solid ${C.hair}`, borderBottom: `1px solid ${C.hair}` }}>
                <div style={{ maxWidth: 960, margin: '0 auto', padding: '56px 20px' }}>
                    <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.7px', textAlign: 'center' }}>기공소 운영에 필요한 기능만 담았습니다</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 32 }}>
                        {FEATURES.map(f => (
                            <div key={f.title} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, padding: '22px 20px' }}>
                                <div style={{ fontSize: 24 }}>{f.icon}</div>
                                <div style={{ fontSize: 15.5, fontWeight: 800, marginTop: 10, letterSpacing: '-0.3px' }}>{f.title}</div>
                                <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.7, marginTop: 6 }}>{f.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 4. 요금 */}
            <div style={{ maxWidth: 960, margin: '0 auto', padding: '56px 20px' }}>
                <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.7px', textAlign: 'center' }}>요금 안내</h2>
                <div style={{ maxWidth: 440, margin: '28px auto 0', background: '#fff', border: `1.5px solid ${C.accent}`, borderRadius: 16, padding: '32px 28px', textAlign: 'center', boxShadow: '0 10px 30px rgba(21,27,38,.07)' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.accent, letterSpacing: 1 }}>거래명세서 자동화 서비스</div>
                    <div style={{ marginTop: 12 }}>
                        <span style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-1.5px' }}>월 33,000원</span>
                        <span style={{ fontSize: 14, color: C.sub, marginLeft: 6 }}>(VAT 포함)</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 20, textAlign: 'left' }}>
                        {['첫 1개월 무료 체험', '카드 등록 없이 시작', '가입만으로는 비용이 발생하지 않음', '체험 종료 다음 날부터 매월 같은 날 자동 결제·갱신 (약정 없음)', '언제든 해지 가능 · 환불은 이용약관 제4조의3에 따름'].map(t => (
                            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, color: C.ink }}>
                                <span style={{ width: 20, height: 20, borderRadius: '50%', background: C.okSoft, color: C.ok, fontSize: 12, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✓</span>
                                {t}
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: 24 }}>
                        {cta('1개월 무료로 시작하기', { width: '100%' })}
                    </div>
                </div>
            </div>

            {/* 5. 하단 문의 */}
            <div style={{ background: C.ink, color: '#fff' }}>
                <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 20px 48px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>궁금한 점이 있으신가요?</div>
                    <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
                        <a href="tel:01029723733" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.25)', borderRadius: 10, padding: '12px 20px', color: '#fff', fontSize: 14.5, fontWeight: 700, textDecoration: 'none' }}>
                            전화 문의 010-2972-3733
                        </a>
                        <a href="https://pf.kakao.com/_xfsREX" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FEE500', borderRadius: 10, padding: '12px 20px', color: '#191919', fontSize: 14.5, fontWeight: 800, textDecoration: 'none' }}>
                            카카오톡 채널 문의
                        </a>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 28, lineHeight: 1.8 }}>
                        덴탈클러스터 · 치과기공소 거래명세서 자동화<br />
                        <a href="/terms" style={{ color: 'rgba(255,255,255,.6)' }}>이용약관</a>
                        {' · '}
                        <a href="/privacy" style={{ color: 'rgba(255,255,255,.6)' }}>개인정보처리방침</a>
                    </div>
                    {/* 사업자정보 — 전자상거래법 표시사항 + 결제사(토스페이먼츠) 심사 요구(상호·대표자·사업자번호·주소 동호수·연락처) 2026-09-23 */}
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.45)', marginTop: 14, lineHeight: 1.8 }}>
                        주식회사 덴탈클러스터 · 대표자 김승우 · 사업자등록번호 318-81-11805<br />
                        주소 {BIZ_ADDRESS} · 전화 {CONTACT_PHONE} · 이메일 {CONTACT_EMAIL}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LabLanding;
